import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { appState } from "@/app/appState";
import {
    formatKroner,
    formatSmallKroner,
    PACE_BRISK,
    PACE_FAST,
    PaceZone,
    paceZone,
    UsageBlockReason,
    UsageMeter,
    usageMeterState,
} from "@/app/usageMeterState";

/** The dial spans pace 0 (far left) to this (far right); 1 points straight up. */
const DIAL_MAX_PACE = 2;
const CX = 20;
const CY = 21;
const R = 16;

const ZONE_STROKE: Record<PaceZone, string> = {
    cruise: "stroke-good",
    brisk: "stroke-warn",
    fast: "stroke-danger",
};

const point = (fraction: number, radius = R) => {
    const theta = Math.PI * (1 - fraction);
    return { x: CX + radius * Math.cos(theta), y: CY - radius * Math.sin(theta) };
};

const arc = (from: number, to: number) => {
    const a = point(from);
    const b = point(to);
    return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${R} ${R} 0 0 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
};

const ZONES: { zone: PaceZone; from: number; to: number }[] = [
    { zone: "cruise", from: 0, to: PACE_BRISK / DIAL_MAX_PACE },
    { zone: "brisk", from: PACE_BRISK / DIAL_MAX_PACE, to: PACE_FAST / DIAL_MAX_PACE },
    { zone: "fast", from: PACE_FAST / DIAL_MAX_PACE, to: 1 },
];

const fuelTone = (fuel: number) => (fuel > 0.25 ? "bg-good" : fuel > 0.1 ? "bg-warn" : "bg-danger");

export const budgetSettingsPath = (orgId: number) => `/workspace/settings/org/${orgId}/billing`;

export const usageBlockMessage = (reason: UsageBlockReason) => appState.t(`meter.blocked.${reason}`);

/**
 * Speedometer: the needle is the spending pace (1 = lands exactly on the
 * ceiling at month end), the strip below is the fuel left this month.
 */
export const UsageGaugeDial = ({ pace, fuel, empty = false }: { pace: number | null; fuel: number; empty?: boolean }) => {
    const fraction = Math.max(0, Math.min(1, (pace ?? 0) / DIAL_MAX_PACE));
    const zone = empty ? "fast" : paceZone(pace);
    return (
        <span className="usage-gauge-dial" aria-hidden="true">
            <svg width="40" height="24" viewBox="0 0 40 24">
                {ZONES.map((z) => (
                    <path key={z.zone} d={arc(z.from, z.to)} className={`usage-gauge-zone ${ZONE_STROKE[z.zone]}`} />
                ))}
                <g className="usage-gauge-needle" style={{ transform: `rotate(${(fraction - 0.5) * 180}deg)`, transformOrigin: `${CX}px ${CY}px` }}>
                    <line x1={CX} y1={CY} x2={CX} y2={CY - R + 4} className={ZONE_STROKE[zone]} />
                </g>
                <circle cx={CX} cy={CY} r={2} className="usage-gauge-hub" />
            </svg>
            <span className="usage-gauge-fuel">
                <span className={`usage-gauge-fuel-level ${fuelTone(fuel)}`} style={{ width: `${Math.round(fuel * 100)}%` }} />
            </span>
        </span>
    );
};

/** The popover body: plain-language numbers for the month. */
export const UsageGaugeDetails = ({
    meter,
    lastCycleCost,
    blockedReason,
    onAdjust,
}: {
    meter: UsageMeter;
    lastCycleCost: number | null;
    blockedReason: UsageBlockReason | null;
    onAdjust?: () => void;
}) => {
    const ceiling = meter.ceiling ?? 0;
    const fuel = ceiling > 0 ? Math.max(0, Math.min(1, (meter.remaining ?? 0) / ceiling)) : 0;
    const zone = paceZone(meter.pace);
    const daysLeft = Math.max(0, Math.ceil(meter.days_left));
    return (
        <div className="usage-gauge-details">
            <small className="caption">{appState.t("meter.title")}</small>
            <p className="usage-gauge-headline">
                {appState.t("meter.spentOf", { spent: formatKroner(meter.spent), ceiling: formatKroner(ceiling) })}
            </p>
            <span className="usage-gauge-fuel usage-gauge-fuel-large">
                <span className={`usage-gauge-fuel-level ${fuelTone(fuel)}`} style={{ width: `${Math.round(fuel * 100)}%` }} />
            </span>
            <p className="detail-text">
                {appState.t("meter.left", { amount: formatKroner(meter.remaining ?? 0) })}
                {" · "}
                {appState.t("meter.daysLeft", { n: daysLeft })}
            </p>
            {blockedReason ? (
                <p className="usage-gauge-line text-danger">{usageBlockMessage(blockedReason)}</p>
            ) : (
                <>
                    <p className={`usage-gauge-line usage-gauge-pace-${zone}`}>{appState.t(`meter.pace.${zone}`)}</p>
                    {meter.sustainable_per_day !== null && (
                        <p className="detail-text">
                            {appState.t("meter.sustainable", { amount: formatSmallKroner(meter.sustainable_per_day) })}
                        </p>
                    )}
                    <p className="detail-text">
                        {appState.t("meter.projection", { amount: formatKroner(meter.projected_month_end) })}
                    </p>
                </>
            )}
            {lastCycleCost !== null && (
                <p className="detail-text">{appState.t("meter.lastMessage", { amount: formatSmallKroner(lastCycleCost) })}</p>
            )}
            <div className="usage-gauge-footer">
                <small className="field-label">{appState.t("meter.exclVat")}</small>
                {onAdjust && (
                    <button type="button" className="link-muted" onClick={onAdjust}>
                        {appState.t("meter.adjust")}
                    </button>
                )}
            </div>
        </div>
    );
};

/** Composer speedometer for the active organisation. Hidden when nothing is metered (demo, super admin). */
export const UsageGauge = observer(() => {
    const navigate = useNavigate();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<{ bottom: number; right: number } | null>(null);
    const orgId = appState.user?.active_organisation?.id;

    useEffect(() => {
        usageMeterState.clear();
        if (!orgId) return;
        void usageMeterState.refresh();
        const onFocus = () => void usageMeterState.refresh();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [orgId]);

    useLayoutEffect(() => {
        if (!open || !buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({ bottom: window.innerHeight - rect.top + 8, right: Math.max(8, window.innerWidth - rect.right) });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onPointer = (e: PointerEvent) => {
            const target = e.target as Node;
            if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        window.addEventListener("pointerdown", onPointer);
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("pointerdown", onPointer);
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const meter = usageMeterState.meter;
    if (!meter || !usageMeterState.metered) return null;

    const blocked = usageMeterState.blockedReason;
    const payer = meter.billing_organisation_id;
    const onAdjust =
        meter.can_edit_ceiling && payer
            ? () => {
                  setOpen(false);
                  navigate(budgetSettingsPath(payer));
              }
            : undefined;

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className="btn btn-ghost usage-gauge"
                title={appState.t("meter.title")}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <UsageGaugeDial pace={meter.pace} fuel={usageMeterState.fuel} empty={blocked === "ceiling_reached"} />
            </button>
            {open &&
                coords &&
                createPortal(
                    <div ref={popoverRef} className="popover usage-gauge-popover" style={{ bottom: coords.bottom, right: coords.right }}>
                        <UsageGaugeDetails
                            meter={meter}
                            lastCycleCost={usageMeterState.lastCycleCost}
                            blockedReason={blocked}
                            onAdjust={onAdjust}
                        />
                    </div>,
                    document.body,
                )}
        </>
    );
});

/** Above the composer when chat is refused: why, and who can fix it. */
export const UsageBlockedBanner = observer(() => {
    const navigate = useNavigate();
    const reason = usageMeterState.blockedReason;
    if (!reason) return null;
    const meter = usageMeterState.meter;
    const payer = meter?.billing_organisation_id ?? appState.user?.active_organisation?.id;
    const canFix = meter ? !!meter.can_edit_ceiling : false;
    return (
        <div className="usage-blocked-banner" role="status">
            <p className="usage-blocked-text">{usageBlockMessage(reason)}</p>
            {canFix && payer ? (
                <button type="button" className="link" onClick={() => navigate(budgetSettingsPath(payer))}>
                    {appState.t(reason === "ceiling_reached" || reason === "no_ceiling" ? "meter.blocked.raise" : "meter.blocked.openBilling")}
                </button>
            ) : (
                <p className="detail-text">{appState.t("meter.blocked.askAdmin")}</p>
            )}
        </div>
    );
});
